/*
 -------------------------------
 DO NOT EDIT THIS FILE
 -------------------------------
 */

import contextFactory from 'yoda-app-shell/lib/contextFactory';
import RestClient from 'yoda-interfaces/lib/RestClient/RestClient';
import { preloadMfeComponents } from 'yoda-app-shell/lib/mfe/getMfeComponent';
import App from '../src/App';
import rootReducer from '../src/reducers';
import rootSaga from '../src/sagas';
import rootActionTypes from '../src/actionTypes';
import rootMiddleware from '../src/middlewares';
import * as micrositeConfig from '../src/config';
import localSettings from './settings';

/**
 * Resolves server-side MFE remote URLs from the runtime moduleFederation context.
 *
 * @param {Object} [settings] - Microsite settings from bootstrap
 * @returns {Object|null} Resolved remote config (name → URL) or null if unavailable
 */
const resolveServerRemotes = (settings) => {
    if (!settings) return null;

    const remotes = settings?.moduleFederation?.common?.remotes?.server
        || settings?.moduleFederation?.remotes?.server
        || null;

    if (!remotes) {
        console.warn('[MFE] No server remotes found in runtime settings');
        return null;
    }

    const resolved = {};
    Object.entries(remotes).forEach(([key, value]) => {
        // "name@url" -> url. Split on the FIRST @ only; the URL may contain one.
        resolved[key] = typeof value === 'string' && value.includes('@')
            ? value.slice(value.indexOf('@') + 1) || value
            : value;
    });

    return Object.keys(resolved).length > 0 ? resolved : null;
};

const history = {};
const reducer = rootReducer;
const micrositeParams = {
    ...micrositeConfig,
    localSettings,
};

/**
 * Preloads MFE components for SSR.
 * Remote URLs are resolved from runtime settings, falling back to build-time defaults.
 *
 * @param {Object} [settings] - Microsite settings with moduleFederation config
 */
const preloadMfe = async (settings) => {
    let federatedComponents;
    let setRuntimeRemotes;
    try {
        const federatedConfig = require('../config/federatedComponents');
        federatedComponents = federatedConfig.federatedComponents;
        setRuntimeRemotes = federatedConfig.setRuntimeRemotes;
    } catch (e) {
        // Loud on purpose: a registry that fails to load silently turns every
        // federated slot into its fallback with nothing in the logs.
        console.error('[MFE] failed to load config/federatedComponents:', e?.message);
        federatedComponents = null;
        setRuntimeRemotes = null;
    }

    if (!federatedComponents) return;

    // Resolve remotes from runtime settings first, fall back to build-time config
    const runtimeRemotes = resolveServerRemotes(settings);
    if (runtimeRemotes && setRuntimeRemotes) {
        setRuntimeRemotes(runtimeRemotes);
    }

    // Header and HeaderV3 are variants of one slot — Layout renders exactly one,
    // picked by enableHeaderV3. Preloading both puts the unused variant's JS+CSS
    // on every page (preloaded exposes get stylesheet links + preload hints).
    const wantsHeaderV3 = Boolean(settings?.featureFlags?.enableHeaderV3);
    const componentPaths = Object.keys(federatedComponents).reduce((acc, key) => {
        if (key.startsWith('__')) return acc;
        const entry = federatedComponents[key];
        if (entry.isSSR === false) return acc;
        if (key === 'Header' && wantsHeaderV3) return acc;
        if (key === 'HeaderV3' && !wantsHeaderV3) return acc;
        acc.push(entry.path);
        return acc;
    }, []);

    const remotes = federatedComponents.__remotes;

    if (componentPaths.length > 0 && remotes) {
        await preloadMfeComponents(componentPaths, remotes);
    }
};

const context = contextFactory(
    App,
    reducer,
    rootSaga,
    rootActionTypes,
    history,
    RestClient,
    micrositeParams,
    rootMiddleware
);

context.preloadMfeComponents = preloadMfe;
/*
 * Registry on the context so yoda-app-shell can resolve the externalized shell
 * on both sides: server.jsx builds the SSR bridge from it after each preload,
 * client.jsx uses it for the pre-hydration preload and the client bridge.
 */
try {
    // eslint-disable-next-line global-require
    context.federatedComponents = require('../config/federatedComponents').federatedComponents;
} catch (e) {
    console.error('[MFE] failed to load config/federatedComponents for context:', e?.message);
    context.federatedComponents = null;
}

export default context;
